import React, { useState, useRef, useEffect } from 'react';
import { Search, Bell, Menu, Settings, LogOut, User, Package, Tags } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useInventory } from '../../context/InventoryContext';
import Button from '../ui/Button';
import Input from '../ui/Input';

interface TopBarProps {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
}

const TopBar: React.FC<TopBarProps> = ({ sidebarOpen, toggleSidebar }) => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { products, categories } = useInventory();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  const notificationRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  // Filter results based on search term
  const searchResults = searchTerm.trim() ? {
    products: products.filter(product => 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase())
    ).slice(0, 5),
    categories: categories.filter(category =>
      category.name.toLowerCase().includes(searchTerm.toLowerCase())
    ).slice(0, 3)
  } : null;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setShowProfile(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowSearchResults(!!value.trim());
  };

  const handleSearchItemClick = (type: 'product' | 'category', id: string) => {
    setShowSearchResults(false);
    setSearchTerm('');
    navigate(type === 'product' ? `/supplies/${id}` : `/categories/${id}`);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/auth');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const notifications = [
    {
      id: 1,
      title: 'Low Stock Alert',
      message: 'Wireless Headphones are running low on stock',
      time: '5 minutes ago',
      unread: true,
    },
    {
      id: 2,
      title: 'New Transaction',
      message: 'Stock adjustment completed for Office Chair',
      type: 'transaction',
      time: '1 hour ago',
      unread: false,
    },
    {
      id: 3,
      title: 'System Update',
      message: 'New features have been added to the dashboard',
      type: 'system',
      time: '2 hours ago',
      unread: false,
    },
  ];

  return (
    <header className="sticky top-0 z-10 flex-shrink-0 flex h-16 bg-white shadow">
      <button
        type="button"
        className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-900 lg:hidden"
        onClick={toggleSidebar}
      >
        <span className="sr-only">Open sidebar</span>
        <Menu className="h-6 w-6" />
      </button>
      <div className="flex-1 px-4 flex justify-between">
        <div className="flex-1 flex">
          <div className="w-full flex md:ml-0" ref={searchRef}>
            <div className="relative w-full text-gray-600 focus-within:text-gray-600">
              <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none pl-3">
                <Search className="h-5 w-5" />
              </div>
              <input
                className="block w-full h-full pl-10 pr-3 py-2 border-transparent text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-0 focus:border-transparent sm:text-sm"
                placeholder="Search supplies, categories..."
                type="search"
                value={searchTerm}
                onChange={handleSearch}
              />
              
              {/* Search Results Dropdown */}
              {showSearchResults && searchResults && (searchResults.products.length > 0 || searchResults.categories.length > 0) && (
                <div className="absolute mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200">
                  {searchResults.products.length > 0 && (
                    <div className="py-2">
                      <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Products</div>
                      {searchResults.products.map(product => (
                        <button
                          key={product.id}
                          className="w-full px-4 py-2 text-sm text-left hover:bg-gray-50 flex items-center"
                          onClick={() => handleSearchItemClick('product', product.id)}
                        >
                          <Package className="h-4 w-4 mr-2 text-gray-600" />
                          <div>
                            <div className="font-medium text-gray-900">{product.name}</div>
                            <div className="text-xs text-gray-500">SKU: {product.sku}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {searchResults.categories.length > 0 && (
                    <div className="py-2 border-t border-gray-100">
                      <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Categories</div>
                      {searchResults.categories.map(category => (
                        <button
                          key={category.id}
                          className="w-full px-4 py-2 text-sm text-left hover:bg-gray-50 flex items-center"
                          onClick={() => handleSearchItemClick('category', category.id)}
                        >
                          <Tags className="h-4 w-4 mr-2 text-gray-600" />
                          <span className="font-medium text-gray-900">{category.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="ml-4 flex items-center md:ml-6 space-x-4">
          {/* Notifications dropdown */}
          <div className="relative" ref={notificationRef}>
            <button
              className="bg-white p-1 rounded-full text-gray-600 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-900"
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <span className="sr-only">View notifications</span>
              <div className="relative">
                <Bell className="h-6 w-6" />
                {notifications.some(n => n.unread) && (
                  <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-400 ring-2 ring-white" />
                )}
              </div>
            </button>

            {showNotifications && (
              <div className="origin-top-right absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="py-1">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <h3 className="text-sm font-medium text-gray-900">Notifications</h3>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`px-4 py-3 hover:bg-gray-50 ${
                          notification.unread ? 'bg-blue-50' : ''
                        }`}
                      >
                        <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                        <p className="text-sm text-gray-500">{notification.message}</p>
                        <p className="text-xs text-gray-600 mt-1">{notification.time}</p>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-gray-100 px-4 py-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      fullWidth
                      onClick={() => {
                        setShowNotifications(false);
                        navigate('/notifications');
                      }}
                    >
                      View all notifications
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Profile dropdown */}
          <div className="relative" ref={profileRef}>
            <button
              className="max-w-xs bg-white flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-900"
              onClick={() => setShowProfile(!showProfile)}
            >
              <span className="sr-only">Open user menu</span>
              <div className="h-8 w-8 rounded-full bg-blue-900 flex items-center justify-center text-white">
                {user?.email?.[0].toUpperCase()}
              </div>
            </button>

            {showProfile && (
              <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="py-1">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user?.email}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowProfile(false);
                      navigate('/settings');
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </button>
                  <button
                    onClick={() => {
                      setShowProfile(false);
                      navigate('/profile');
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                  >
                    <User className="h-4 w-4 mr-2" />
                    Profile
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50 flex items-center"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default TopBar;